import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import SelectBox from '@adsk/alloy-select-box';
import FormField from '@adsk/alloy-react-form-field';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import {
  ACCESS_LEVELS,
  COMPONENT_VARIANTS,
  FLAG_NAMES,
  SERVICES,
} from '@adsk/acs-admin-ui-components-helpers';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import { useAdminFeatureFlag } from '../../../AdminLaunchDarklyProvider';
import { useStore } from '../../../StoreProvider';
import { fieldStyle } from '../../styles';
import { ACTIONS } from '../../state/actions';
import Label from '../../../Label';
import { infoIconStyle, infoIconWrapperStyles } from './styles';

const {
  ADMIN_FLAGS: { ENABLE_PROJECT_ROLES_PRODUCTS },
} = FLAG_NAMES;

const RoleField = ({ roles, onChangeRoleInput, variant, isEmsAccount }) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { value: enableProjectRolesProducts } = useAdminFeatureFlag(
    ENABLE_PROJECT_ROLES_PRODUCTS
  );
  const { state, dispatch } = useStore();
  const { selectedRole } = state;
  const showDefaultAccessLevelInfo =
    selectedRole?.serviceAccesses || selectedRole?.products;
  const showAdminDefaultAccessLevelInfo = useMemo(() => {
    if (selectedRole?.serviceAccesses && !enableProjectRolesProducts) {
      return selectedRole.serviceAccesses.some(
        (service) =>
          service.serviceName === SERVICES.PROJECT_ADMIN && service.access
      );
    }
    if (selectedRole?.products && enableProjectRolesProducts) {
      return selectedRole.products.some(
        (product) =>
          product.key === PRODUCT_IDS.PROJECT_ADMINISTRATION &&
          product.accessLevel === ACCESS_LEVELS.PROJECT_ADMIN
      );
    }

    return false;
  }, [selectedRole, enableProjectRolesProducts]);

  const handleRoleChange = (value) => {
    dispatch({
      type: ACTIONS.SET_ROLE,
      payload: value,
    });
  };

  const handleRoleInputChange = (value) => {
    dispatch({
      type: ACTIONS.SET_ROLE_SEARCH_INPUT,
      payload: value,
    });
  };

  const latestOnChangeRoleInput = useRef(onChangeRoleInput);

  useEffect(() => {
    latestOnChangeRoleInput.current = onChangeRoleInput;
  }, [onChangeRoleInput]);

  useEffect(() => {
    if (!latestOnChangeRoleInput.current) return;

    const handler = setTimeout(() => {
      latestOnChangeRoleInput.current(state.roleSearchInput.trim());
    }, 800);

    return () => clearTimeout(handler);
  }, [state.roleSearchInput]);

  const label =
    variant !== COMPONENT_VARIANTS.PROJECT ? (
      T.translate('ACCOUNT_ROLE_LABEL')
    ) : (
      <Label
        required
        text={T.translate('ROLE_LABEL')}
        tooltipMessage={
          isEmsAccount
            ? T.translate('ROLE_LABEL_INFO_EMS')
            : T.translate('ROLE_LABEL_INFO')
        }
        size={13}
        labelStyle={{ paddingBottom: '0px' }}
      />
    );

  return (
    <FormField
      label={label}
      labelVariant={FormField.LABEL_VARIANTS.TOP}
      style={fieldStyle}
      data-testid="InviteProjectMemberModal_role_field"
    >
      <SelectBox
        clearable={variant !== COMPONENT_VARIANTS.PROJECT}
        ignoreCase
        labelKey="name"
        subtextKey={variant === COMPONENT_VARIANTS.PROJECT && 'description'}
        maxMenuHeight={200}
        onChange={handleRoleChange}
        onInputChange={handleRoleInputChange}
        options={roles}
        placeholder={T.translate('ROLE_PLACEHOLDER')}
        searchable
        value={state.selectedRole}
        valueKey="id"
      />
      {variant === COMPONENT_VARIANTS.PROJECT && showDefaultAccessLevelInfo && (
        <div style={infoIconWrapperStyles}>
          <AlertInformationIcon size={13} style={infoIconStyle} />
          {T.translate(
            showAdminDefaultAccessLevelInfo
              ? 'ROLE_ADMIN_DEFAULT_ACCESS_LEVEL_INFO'
              : 'ROLE_MEMBER_DEFAULT_ACCESS_LEVEL_INFO'
          )}
        </div>
      )}
    </FormField>
  );
};

RoleField.propTypes = {
  isEmsAccount: PropTypes.bool,
  onChangeRoleInput: PropTypes.func,
  roles: PropTypes.array,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

RoleField.defaultProps = {
  isEmsAccount: false,
};

export default RoleField;
